/*
 * Cart "save my bag" email capture (see snippets/cart-save-bag.liquid). Live since 2026-09-14.
 * The form is injected into the cart notification after page load, so this listens with event delegation.
 * Klaviyo client-side endpoints use the PUBLIC site id only (never a private key):
 *   1. POST /client/subscriptions  -> Email List T6gyac with email marketing consent (same list as the popup)
 *   2. POST /client/events         -> metric "Saved Bag" carrying the cart items, a cart-restore link and the traffic
 *                                     source, so the Pinterest checkpoint can count bags saved by ad visitors whose
 *                                     checkout later arrives through the email link instead of the tagged visit.
 * This file loads on every page (snippets/cart-notification.liquid), so it also records the landing source, and
 * credits sign-ups from the Shopify Forms popup to that source (see "Shopify Forms sign-up popup" below).
 */
(() => {
  const COMPANY_ID = 'SMDLVF';
  const LIST_ID = 'T6gyac';
  const REVISION = '2026-07-15';
  const BASE = 'https://a.klaviyo.com/client';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const TOUCH_SESSION_KEY = 'museori_touch_session';
  const TOUCH_LAST_KEY = 'museori_touch_last';
  const OWN_HOSTS = /(^|\.)(museori\.com|myshopify\.com|shopify\.com|shop\.app)$/;
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];

  const loadJSON = (area, key) => {
    try {
      return JSON.parse(window[area].getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  };

  const saveJSON = (area, key, value) => {
    try {
      window[area].setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage blocked (private mode, site data off): the bag still saves, only without a source.
    }
  };

  // A landing counts when it carries UTM tags, a Pinterest click id (epik), or an external referrer
  // (pinterest.com, android-app://com.pinterest, google...). Internal page views return null.
  const readTouch = (href, referrer) => {
    const url = new URL(href);
    let referrerHost = '';
    try {
      referrerHost = referrer ? new URL(referrer).host : '';
    } catch (error) {
      referrerHost = String(referrer).slice(0, 100);
    }
    const external = Boolean(referrerHost) && !OWN_HOSTS.test(referrerHost);
    const touch = {};
    UTM_KEYS.forEach((key) => {
      const value = url.searchParams.get(key);
      if (value) touch[key] = value.slice(0, 200);
    });
    const clickId = url.searchParams.has('epik');
    // Paid Google clicks carry gclid / gbraid / wbraid; free listings share their UTMs and carry none.
    const googleClickId = ['gclid', 'gbraid', 'wbraid'].some((key) => url.searchParams.has(key));
    if (!Object.keys(touch).length && !external && !clickId && !googleClickId) return null;
    return {
      ...touch,
      referrer_host: external ? referrerHost : '',
      pinterest_click_id: clickId,
      google_click_id: googleClickId,
      google_campaign_id: (url.searchParams.get('gad_campaignid') || '').slice(0, 30),
      landing_path: url.pathname,
      at: new Date().toISOString(),
    };
  };

  try {
    const touch = readTouch(window.location.href, document.referrer);
    if (touch) {
      if (!loadJSON('sessionStorage', TOUCH_SESSION_KEY)) saveJSON('sessionStorage', TOUCH_SESSION_KEY, touch);
      saveJSON('localStorage', TOUCH_LAST_KEY, touch);
    }
  } catch (error) {
    // Never let source tracking break the page.
  }

  // First external landing of this browser session; else the most recent one on this device.
  const sourceProperties = () => {
    const session = loadJSON('sessionStorage', TOUCH_SESSION_KEY);
    const touch = session || loadJSON('localStorage', TOUCH_LAST_KEY);
    if (!touch) return { source_scope: 'none' };
    return {
      source_scope: session ? 'this_session' : 'earlier_visit',
      utm_source: touch.utm_source || '',
      utm_medium: touch.utm_medium || '',
      utm_campaign: touch.utm_campaign || '',
      utm_content: touch.utm_content || '',
      referrer_host: touch.referrer_host || '',
      pinterest_click_id: Boolean(touch.pinterest_click_id),
      google_click_id: Boolean(touch.google_click_id),
      google_campaign_id: touch.google_campaign_id || '',
      landing_path: touch.landing_path || '',
      source_at: touch.at || '',
    };
  };

  const post = async (path, body) => {
    const res = await fetch(`${BASE}/${path}/?company_id=${COMPANY_ID}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Klaviyo ${path} ${res.status}`);
    return res;
  };

  const readBag = (root) => {
    const node = root.querySelector('[data-save-bag-data]');
    try {
      return node ? JSON.parse(node.textContent) : {};
    } catch (error) {
      return {};
    }
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-save-bag-form]');
    if (!form) return;
    event.preventDefault();

    const root = form.closest('[data-save-bag]');
    const input = form.querySelector('input[type="email"]');
    const status = form.querySelector('[data-save-bag-status]');
    const button = form.querySelector('button[type="submit"]');
    const email = (input.value || '').trim();

    if (!EMAIL_RE.test(email)) {
      status.textContent = 'Enter a valid email address.';
      input.focus();
      return;
    }

    button.disabled = true;
    status.textContent = 'Saving...';
    const bag = readBag(root);

    try {
      await post('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Cart save bag',
            profile: {
              data: {
                type: 'profile',
                attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: LIST_ID } } },
        },
      });

      await post('events', {
        data: {
          type: 'event',
          attributes: {
            properties: {
              items: bag.items || [],
              bag_url: bag.bag_url,
              item_count: bag.item_count,
              ...sourceProperties(),
            },
            value: bag.value,
            value_currency: bag.currency || 'USD',
            unique_id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            metric: { data: { type: 'metric', attributes: { name: 'Saved Bag' } } },
            profile: { data: { type: 'profile', attributes: { email } } },
          },
        },
      });

      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify({ email });
      }

      root.classList.add('cart-save-bag--done');
      status.textContent = 'Saved. Check your inbox for a link back to your bag.';
    } catch (error) {
      button.disabled = false;
      status.textContent = 'Something went wrong. Please try again.';
    }
  });

  /*
   * Shopify Forms sign-up popup (app embed <shopify-forms-embed>, open shadow root). Shopify creates the customer and
   * syncs it to Klaviyo with no visit data, so a popup subscriber has no source. On a successful sign-up this
   * identifies the browser to Klaviyo and tracks "Popup Signup" with the same source properties as Saved Bag.
   * It never subscribes anyone: Shopify already does that, with the consent the shopper gave in its own form.
   * Success = the app's own state: its root element carries data-current-step = tease | form | success | error
   * (the Forms bundle's step enum, lowercased). Only "success" after a valid submit counts; "error", a close
   * (the app sets the host to display:none) or 30 s without success never do. One report per popup.
   */
  const POPUP_HOST = 'shopify-forms-embed';
  const POPUP_WAIT_MS = 30000;
  const watchedRoots = new WeakSet();

  const reportPopupSignup = (email, firstName, formId) => {
    const klaviyo = window.klaviyo;
    if (!klaviyo) return;
    const profile = firstName ? { email, first_name: firstName } : { email };
    const properties = { form_id: formId, form_type: 'Shopify Forms popup', ...sourceProperties() };
    if (typeof klaviyo.identify === 'function') {
      Promise.resolve(klaviyo.identify(profile))
        .then(() => klaviyo.track('Popup Signup', properties))
        .catch(() => {});
    } else if (typeof klaviyo.push === 'function') {
      klaviyo.push(['identify', profile]);
      klaviyo.push(['track', 'Popup Signup', properties]);
    }
  };

  const watchPopup = (host) => {
    const root = host.shadowRoot;
    if (!root || watchedRoots.has(root)) return;
    watchedRoots.add(root);
    const formId = (host.id.match(/(\d+)$/) || [])[1] || '';
    let latestSubmit = 0;
    let reported = false;

    root.addEventListener('submit', (event) => {
      const form = event.target;
      if (reported || !form || form.tagName !== 'FORM') return;
      const emailInput = form.querySelector('#email, [data-testid="field-email"]');
      const email = ((emailInput && emailInput.value) || '').trim();
      if (!EMAIL_RE.test(email)) return;
      const nameInput = form.querySelector('#first_name, [data-testid="field-first_name"]');
      const firstName = ((nameInput && nameInput.value) || '').trim().slice(0, 100);
      const submitId = ++latestSubmit;
      const started = Date.now();

      const check = () => {
        if (reported || submitId !== latestSubmit) return;
        const appRoot = root.querySelector('[data-current-step]');
        const step = appRoot ? appRoot.getAttribute('data-current-step') : '';
        if (step === 'success') {
          reported = true;
          reportPopupSignup(email, firstName, formId);
          return;
        }
        if (step === 'error' || host.style.display === 'none' || Date.now() - started > POPUP_WAIT_MS) return;
        window.setTimeout(check, 250);
      };
      window.setTimeout(check, 250);
    }, true);
  };

  const findPopups = () => {
    try {
      document.querySelectorAll(POPUP_HOST).forEach(watchPopup);
    } catch (error) {
      // Never let source tracking break the page.
    }
  };
  findPopups();
  window.setInterval(findPopups, 2000);
})();
